/**
 * TikTok / EulerStream event parser
 * Handles RAW Euler WebSocket messages with NO pre-processing
 */

/**
 * Parse TikTok/EulerStream WebSocket event
 * @param {object} event - FULL RAW Euler message: { type: 'WebcastXXX', data: {...} }
 * @returns {object|null} Normalized event
 */
function parseTikTokEvent(event) {
  if (!event) return null;
  
  const platform = 'tiktok';
  
  // Handle FULL Euler message structure
  // Euler messages have: { type: 'WebcastChatMessage', data: {...} }
  const eulerType = event.type;
  const eventData = event.data || event;  // Support both full and data-only for compatibility
  
  // Determine event type from Euler type or infer from data
  let eventType = null;
  if (eulerType) {
    const type = eulerType.toLowerCase();
    if (type.includes('chat') || type.includes('comment') || type === 'comment' || type === 'chat') {
      eventType = 'comment';
    } else if (type.includes('gift') || type === 'gift') {
      eventType = 'gift';
    } else if (type.includes('like') || type === 'like') {
      eventType = 'like';
    } else if (type.includes('member') || type === 'member') {
      eventType = 'join';
    } else if (type.includes('social')) {
      // WebcastSocialMessage - check action field to determine type
      if (eventData.action === '1' || eventData.action === 1) {
        eventType = 'follow';
      } else if (eventData.action === '3' || eventData.action === 3) {
        eventType = 'share';
      }
    }
  }
  // If no explicit type, infer from fields
  if (!eventType) {
    if (eventData.comment || eventData.message) eventType = 'comment';
    else if (eventData.giftDetails || eventData.giftName || eventData.repeatCount) eventType = 'gift';
    else if (eventData.likeCount !== undefined) eventType = 'like';
    else if (eventData.memberCount !== undefined) eventType = 'join';
    else if ((eventData.action === '1' || eventData.action === 1) && eventData.followCount !== undefined) eventType = 'follow';
    else if ((eventData.action === '3' || eventData.action === 3) && eventData.shareCount !== undefined) eventType = 'share';
  }
  
  if (!eventType) return null;
  
  // Extract user info from RAW Euler structure (inside data field)
  const userInfo = eventData.user || {};
  const user = {
    username: userInfo.uniqueId || userInfo.nickname || eventData.username || 'Viewer',
    displayName: userInfo.displayName || userInfo.nickname || eventData.displayName,
    imageUrl: extractImageUrl(userInfo) || eventData.imageUrl,
    userId: userInfo.id || userInfo.userId || eventData.userId
  };
  
  // Common fields
  const base = {
    platform,
    user,
    id: eventData.id || event.id || `tiktok_${Date.now()}_${Math.random()}`,
    timestamp: extractTimestamp(eventData)
  };
  
  switch (eventType) {
    case 'comment':
      return {
        ...base,
        type: 'comment',
        message: eventData.comment || eventData.message || eventData.text || ''
      };
      
    case 'gift':
      const giftDetails = eventData.giftDetails || eventData.gift || {};
      return {
        ...base,
        type: 'gift',
        giftName: giftDetails.giftName || giftDetails.name || eventData.giftName || 'Gift',
        giftCount: eventData.repeatCount || eventData.giftCount || eventData.count || 1,
        value: giftDetails.diamondCount || eventData.diamondCount || eventData.value || 0,
        totalValue: calculateTotalValue(giftDetails, eventData)
      };
      
    case 'like':
      return {
        ...base,
        type: 'like',
        likeCount: eventData.likeCount || eventData.count || 1,
        totalLikeCount: eventData.totalLikeCount || eventData.total || eventData.roomLikeCount
      };
      
    case 'join':
      return {
        ...base,
        type: 'join',
        memberCount: eventData.memberCount || 0,
        action: eventData.action || 1  // 1 = join, could be other actions in future
      };
      
    case 'follow':
      return {
        ...base,
        type: 'follow',
        followCount: eventData.followCount || 0
      };
      
    case 'share':
      return {
        ...base,
        type: 'share',
        shareCount: eventData.shareCount || 0,  // Total shares from TikTok
        totalShareCount: eventData.shareCount || 0  // Also store as total for clarity
      };
      
    default:
      return null;
  }
}

/**
 * Extract image URL from raw Euler user object
 */
function extractImageUrl(userInfo) {
  if (!userInfo) return null;
  
  // Try various Euler field structures
  if (userInfo.profilePicture?.url?.[0]) return userInfo.profilePicture.url[0];
  if (userInfo.profilePicture?.urls?.[0]) return userInfo.profilePicture.urls[0];
  if (userInfo.avatar_url) return userInfo.avatar_url;
  if (userInfo.avatarUrl) return userInfo.avatarUrl;
  if (userInfo.avatar?.urls?.[0]) return userInfo.avatar.urls[0];
  
  return null;
}

/**
 * Extract timestamp from raw Euler event
 */
function extractTimestamp(event) {
  if (event.event?.createTime) return parseInt(event.event.createTime);
  if (event.timestamp) return typeof event.timestamp === 'number' ? event.timestamp : Date.now();
  return Date.now();
}

/**
 * Calculate total gift value
 */
function calculateTotalValue(giftDetails, event) {
  const diamondCount = giftDetails.diamondCount || event.diamondCount || event.value || 0;
  const repeatCount = event.repeatCount || event.giftCount || 1;
  return event.totalDiamondCount || (diamondCount * repeatCount);
}

export { parseTikTokEvent };
